import type { UIMessage } from "@ai-sdk/react"

import { MESSAGE_PART_TYPES } from "~components/shared/chat/chat-helpers"

export function toUiMessages(
    messages: readonly AssistantConversationMessage[]
): UIMessage[] {
    return [...messages].reverse().map((message) => ({
        id: message.id,
        role: toUiMessageRole(message.role),
        parts: toUiMessageParts(message),
        metadata: {},
    }))
}

export type AssistantConversation = {
    readonly id: string
    readonly title: string | null
    readonly last_message_at: number | null
    readonly last_message_preview: string | null
    readonly message_count: number
    readonly created_at: number
}

export type AssistantConversationsQueryResult = {
    readonly aiConversations: {
        readonly data: AssistantConversation[]
        readonly count: number
    }
}

export type AssistantConversationMessagesQueryResult = {
    readonly aiConversationMessages: {
        readonly count: number
        readonly data: AssistantConversationMessage[]
        readonly pages: number
    }
}

type AssistantConversationMessage = {
    readonly id: string
    readonly role: AssistantConversationMessageRole
    readonly text: string | null
    readonly created_at: number
    readonly parts: readonly AssistantConversationMessagePart[]
}

type AssistantConversationMessageRole = "USER" | "ASSISTANT" | "SYSTEM" | "TOOL"

type AssistantConversationMessagePart =
    | {
          readonly type: "text"
          readonly text: string
      }
    | {
          readonly type: "file"
          readonly media_type: string
          readonly url: string
          readonly filename: string | null
      }
    | {
          readonly type: string
          readonly data?: unknown
      }

export function mergeConversationPages(
    previousResult: AssistantConversationsQueryResult,
    {
        fetchMoreResult,
    }: {
        readonly fetchMoreResult?: AssistantConversationsQueryResult
    }
): AssistantConversationsQueryResult {
    if (!fetchMoreResult) return previousResult

    return {
        aiConversations: {
            ...fetchMoreResult.aiConversations,
            data: [
                ...previousResult.aiConversations.data,
                ...fetchMoreResult.aiConversations.data,
            ],
        },
    }
}

function toUiMessageRole(role: AssistantConversationMessageRole) {
    if (role === "ASSISTANT") return "assistant"
    if (role === "SYSTEM") return "system"
    if (role === "TOOL") return "assistant"

    return "user"
}

function toUiMessageParts(
    message: AssistantConversationMessage
): UIMessage["parts"] {
    const parts = message.parts.flatMap(toUiMessagePart)
    if (parts.length) return parts
    if (!message.text) return []

    return [{ type: MESSAGE_PART_TYPES.TEXT, text: message.text }]
}

function toUiMessagePart(
    part: AssistantConversationMessagePart
): UIMessage["parts"] {
    if (part.type === "text" && "text" in part) {
        return [{ type: MESSAGE_PART_TYPES.TEXT, text: part.text }]
    }

    if (part.type === "file" && "url" in part) {
        return [
            {
                type: MESSAGE_PART_TYPES.FILE,
                mediaType: part.media_type,
                url: part.url,
                filename: part.filename ?? undefined,
            },
        ]
    }

    return []
}
